/**
 * 模組頁面代碼 : 1340-UI-02
 * 頁面說明 : 帳號管理新增修改視窗
 * @module web/modules/role-permission/components/UserModal
 */

// ----- import from npm modules -----
import React, { useState, useEffect } from 'react';
import CryptoJS from 'crypto-js';

// ----- import from local modules -----
import PasswordInput from '../../../components/PasswordInput';

// ----- constants -----
const USER_NEW = 'new';
const USER_EDIT = 'edit';

const RESPONSE_OK = 200;
const TITLE_USER_NEW = '新增帳號';
const TITLE_USER_EDIT = '修改帳號';

const FIXED_SALT = process.env.REACT_APP_FIXED_SALT;

const UserModal = React.forwardRef((props, ref) => {
    const { mode, roleSelection, buildingSelection, positionSelection, user, searchUser } = props;

    let modalIcon = mode === USER_NEW? '#ico-plus' : '#ico-edit';
    let modalTitleText = mode === USER_NEW? TITLE_USER_NEW : TITLE_USER_EDIT;

    // 初始化state
    const [formValues, setFormValues] = useState({
        modalUserAccount: '',
        modalUserRole: '',
        modalUserBuildings: '',
        modalNewPsw: '',
        modalNewPswConfirm: '',
        modalAccountName: '',
        modalUserPosition: '',
        modalUserMemo: '',
    });
    
    const [isAccountDisabled, setIsAccountDisabled] = useState(false);  // 初始化帳號欄位是否Disable
    const [isPasswordDisabled, setIsPasswordDisabled] = useState(false);  // 初始化密碼輸入區塊是否顯示
    const [invalidShow, setInvalidShow] = useState('none');

    // reset PasswordInput
    const [resetPasswordInput, setResetPasswordInput] = useState(false);
    const [resetconfirmPasswordInput, setResetConfirmPasswordInput] = useState(false);

    // Handle change event
    const handleChange = (event) => {
        const target = event.target;
        let value = target.value;

        if (target.type === 'select-multiple') {
            value = target.selectedOptions ? Array.from(target.selectedOptions, option => option.value) : [];
        }

        setFormValues({
            ...formValues,
            [target.name]: value,
        });
    };

    // 使用useEffect在user改變才更新formValues
    useEffect(() => {
        setFormValues({
            modalUserAccount: user? user.account : '',
            modalUserRole: user? user.roleId : '',
            modalUserBuildings: user && Array.isArray(user.buildingIds) ? user.buildingIds : [],
            modalNewPsw: '',
            modalNewPswConfirm: '',
            modalAccountName: user? user.userName : '',
            modalUserPosition: user? user.positionId : '',
            modalUserMemo: user? user.memo : '',
        });
        // 如果修改模式，則設定帳號及密碼欄位要Disable
        setIsAccountDisabled(mode === USER_EDIT? true : false);
        setIsPasswordDisabled(mode === USER_EDIT? true : false);
        setInvalidShow('none');
    }, [user]);

    // 使用useEffect在formValues.modalPasswordPsw改變時更新消息
    useEffect(() => {
        if (formValues.modalNewPswConfirm !== '' && formValues.modalNewPsw === formValues.modalNewPswConfirm) {
            setResetConfirmPasswordInput(true);
        }
    }, [formValues.modalNewPsw]);

    // 使用useEffect在formValues.modalNewPswConfirm
    useEffect(() => {
        if (formValues.modalNewPsw !== '' && formValues.modalNewPsw === formValues.modalNewPswConfirm) {
            setResetPasswordInput(true);
        }
    }, [formValues.modalNewPswConfirm]);

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const form = e.currentTarget;

        // 檢查表單是否驗證通過
        if (form.checkValidity() === false) {
            setInvalidShow('block');
            form.classList.add('was-validated');
            return;
        }

        // 建立 FormData 讀取表单的值
        const formData = new FormData(form);

        // 將密碼欄位的值加密
        const psw = formData.get('modalNewPsw');
        if (psw) {
            const encryptedPsw = CryptoJS.SHA256(psw + FIXED_SALT).toString();
            formData.set('modalNewPsw', encryptedPsw);
        }

        // 建立 user 物件
        const saveUser = {
            userId: mode === USER_NEW? null : user.userId,
            account: formData.get('modalUserAccount'),
            roleId: parseInt(formData.get('modalUserRole')),
            buildingIds: formData.getAll('modalUserBuildings'),
            password: formData.get('modalNewPsw'),
            userName: formData.get('modalAccountName'),
            positionId: parseInt(formData.get('modalUserPosition')),
            memo: formData.get('modalUserMemo'),
        };

        // 呼叫父層元件的新增或修改角色函式
        if (mode === USER_NEW) {
            // 调用 props.addUser 方法
            props.addUser(saveUser).then((response) => {
                const statusCode = response.payload.code;
                if (statusCode !== RESPONSE_OK) {
                    $(ref.current).modal('hide');
                    props.onErrorHandle(TITLE_USER_NEW, response.payload );
                } else {
                    // 调用 fetchUserList and Hide the modal
                    const { buildingId, userName, roleId } = searchUser;
                    setTimeout(() => {
                        props.fetchUserList(buildingId, userName, roleId).then(() => {
                            props.setNeedAccountManagementLoad(true);
                            $(ref.current).modal('hide');
                        });
                    }, 1000); // 1 second delay
                }
            });
        } else if (mode === USER_EDIT) {
            // 調用 props.editUser 方法
            props.editUser(saveUser).then((response) => {
                const statusCode = response.payload.code;
                if (statusCode !== RESPONSE_OK) {
                    $(ref.current).modal('hide');
                    props.onErrorHandle(TITLE_USER_EDIT, response.payload );
                } else {
                    // 調用 fetchUserList and Hide the modal
                    const { buildingId, userName, roleId } = searchUser;
                    props.fetchUserList(buildingId, userName, roleId).then(() => {
                        props.setNeedAccountManagementLoad(true);
                        $(ref.current).modal('hide');
                    });
                }
            });
        }
    };

    return (
        <div ref={ref}
            className="modal fade" id="modalUser" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="modalAccountLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal__form modal-content needs-validation" id="modalAccountForm" onSubmit={handleSubmit}>
                    <div className="modal-header">
                        <h1 className="modal-title fs-5" id="modalAccountLabel">
                            <div className="modal__title__badge">
                                <svg className="badge__icon" aria-hidden="true">
                                    <use href={modalIcon}></use>
                                </svg>
                            </div>
                            <div className="modal__title__text">{modalTitleText}</div>
                        </h1>
                        <button type="button" className="btn-close btn-cancel" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="d-flex flex-column gap-3 px-2">
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">帳號資訊</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-12">
                                            <label htmlFor="modalUserAccount" className="form-label">帳號*</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalUserAccount"
                                                name="modalUserAccount"
                                                value={formValues.modalUserAccount}
                                                onChange={handleChange}
                                                disabled={isAccountDisabled}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalUserRole" className="form-label">角色*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalUserRole"
                                                name="modalUserRole"
                                                value={formValues.modalUserRole}
                                                onChange={handleChange}
                                                required>
                                                    <option disabled value="">請選擇</option>
                                                    {roleSelection.map((role, index) => (
                                                        <option key={index} value={role.roleId}>{role.roleName}</option>
                                                    ))}
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalUserBuildings" className="form-label">管理大樓*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalUserBuildings"
                                                name="modalUserBuildings"
                                                value={Array.isArray(formValues.modalUserBuildings) ? formValues.modalUserBuildings : [formValues.modalUserBuildings]}
                                                onChange={handleChange}
                                                required
                                                multiple>
                                                    <option disabled value="">請選擇</option>
                                                    <option value="0">全部</option>
                                                    {buildingSelection.map((building, index) => (
                                                        <option key={index} value={building.buildingId}>{building.buildingName}</option>
                                                    ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {isPasswordDisabled? null : (
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">密碼</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalNewPsw" className="form-label">密碼*</label>
                                            <PasswordInput
                                                id="modalNewPsw"
                                                name="modalNewPsw"
                                                value={formValues.modalNewPsw}
                                                confirmValue={formValues.modalNewPswConfirm}
                                                onChange={handleChange}
                                                validateType="password"
                                                placeholder=""
                                                defaultMassage="密碼長度為8-16碼，至少包含1個以上大寫英文、小寫英文、數字、符號"
                                                resetMessage={resetPasswordInput}
                                                setResetMessage={(bReset) => setResetPasswordInput(bReset)}        
                                                required
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalNewPswConfirm" className="form-label">確認密碼*</label>
                                            <PasswordInput
                                                id="modalNewPswConfirm"
                                                name="modalNewPswConfirm"
                                                value={formValues.modalNewPswConfirm}
                                                confirmValue={formValues.modalNewPsw}
                                                onChange={handleChange}
                                                validateType="confirmPassword"
                                                placeholder=""
                                                defaultMassage="請確認與輸入密碼相符"
                                                resetMessage={resetconfirmPasswordInput}
                                                setResetMessage={(bReset) => setResetConfirmPasswordInput(bReset)}        
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>)}
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">使用者資訊</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalAccountName" className="form-label">姓名*</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalAccountName"
                                                name="modalAccountName"
                                                value={formValues.modalAccountName}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalUserPosition" className="form-label">職位*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalUserPosition"
                                                name="modalUserPosition"
                                                value={formValues.modalUserPosition}
                                                onChange={handleChange}
                                                required>
                                                <option disabled value="">請選擇</option>
                                                    {positionSelection.map((position, index) => (
                                                        <option key={index} value={position.positionId}>{position.positionName}</option>
                                                    ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">其他</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-12">
                                            <label htmlFor="modalUserNote" className="form-label">備註</label>
                                            <textarea
                                                className="form-control"
                                                id="modalUserMemo"
                                                name="modalUserMemo"
                                                value={formValues.modalUserMemo}
                                                onChange={handleChange}
                                                rows="2">
                                            </textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <div className="alert alert-warning global-invalid-feedback" style={{ display: invalidShow }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-cancel" data-bs-dismiss="modal">取消</button>
                        <button type="submit" className="btn btn-primary">儲存</button>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default UserModal;